import axios from 'axios'
import { Notification, MessageBox, Message, Loading } from 'element-ui'
import store from '@/store'
import { getToken, removeToken } from '@/utils/auth'
import errorCode from '@/utils/errorCode'
import { hintGlobal } from '@/utils/generator/html'
import { tansParams, blobValidate } from "@/utils/ruoyi";
import cache from '@/plugins/cache'
import { saveAs } from 'file-saver'
import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt/bin/jsencrypt.min'

let isPrivate = false;

let downloadLoadingInstance;
// 是否显示重新登录
export let isRelogin = { show: false };

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'

function normalizePrivateKey(privateKey) {
  return String(privateKey || '').trim().replace(/^['"]|['"]$/g, '')
}

function parseResponseText(text) {
  const trimmedText = String(text || '').trim()
  if (!trimmedText) {
    return text
  }
  if (trimmedText.charAt(0) !== '{' && trimmedText.charAt(0) !== '[') {
    return text
  }
  return JSON.parse(trimmedText)
}

function getEncryptKey(headers) {
  return headers['encrypt-key'] || headers['Encrypt-Key']
}

function getPrivateKeys() {
  const privateKeys = [
    process.env.VUE_APP_PRIVATE_KEY,
    process.env.VUE_APP_PRIVATE_KEY_FORMAL
  ].map(normalizePrivateKey).filter(Boolean)

  return privateKeys.filter((privateKey, index) => privateKeys.indexOf(privateKey) === index)
}

function decryptEncryptKey(encryptKey) {
  const privateKeys = getPrivateKeys()
  if (!privateKeys.length) {
    throw new Error('缺少响应解密私钥配置')
  }

  for (let index = 0; index < privateKeys.length; index++) {
    const encryptor = new JSEncrypt()
    encryptor.setPrivateKey(privateKeys[index])
    const key = encryptor.decrypt(encryptKey)
    if (key) {
      return key
    }
  }

  throw new Error('响应密钥解密失败')
}

function decryptResponseData(res) {
  const encryptKey = getEncryptKey(res.headers || {})
  if (!encryptKey) {
    return res.data
  }

  const key = decryptEncryptKey(encryptKey)
  const keyHex = atob(key)
  const decrypted = CryptoJS.AES.decrypt(String(res.data), CryptoJS.enc.Utf8.parse(keyHex), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })
  const decryptedText = CryptoJS.enc.Utf8.stringify(decrypted)
  if (!decryptedText) {
    throw new Error('响应内容解密失败')
  }

  return parseResponseText(decryptedText)
}

function isBlobResponse(res) {
  return res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer'
}

function isSkillCenterPage() {
  return window.location.hash.indexOf('/skillCenter/') !== -1
}

function createAuthError(message) {
  const error = new Error(message || '登录状态已失效')
  error.code = 401
  return error
}

function rejectSkillCenterAuth(message) {
  removeToken()
  window.clinicalAppTokenReady = false
  setTimeout(() => {
    window.dispatchEvent(new Event('skillCenterAuthExpired'))
  }, 0)
  return Promise.reject(createAuthError(message))
}


function createAxios( baseURL ){
  const service = axios.create({
    // axios中请求配置有baseURL选项，表示请求URL公共部分
    baseURL,
    // 超时
    timeout: 10000
  })

  // request拦截器
  service.interceptors.request.use(config => {
    // 是否需要设置 token
    const isToken = (config.headers || {}).isToken === false
    // 是否需要防止数据重复提交
    const isRepeatSubmit = (config.headers || {}).repeatSubmit === false
    // const tokenz = 'eyJhbGciOiJIUzUxMiJ9.eyJhY2NvdW50X2lkIjozMDUyLCJ1c2VyX2lkIjozNjYzLCJvcmdfaWQiOjQ2LCJ1c2VyX2tleSI6IjlmNWQzMDFlLTdmNmItNDhlMC1iMGJiLTA2Y2EwNzk1MzhjYSIsImRldmljZV90eXBlIjoiYXBwLXNraWxsIiwidXNlcm5hbWUiOiIxMzUxMTExMTExMSJ9.RxhphO6nCwkWJ6ZquJ4putHViPanGSdN89mCkJ6hD6uV5HBf9czVvQi6pROV7TkFmh3snVfvyZQGH5Ai6gsWDA'
    if (getToken() && !isToken) {
      config.headers['Authorization'] = 'Bearer ' + getToken() // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // config.headers['Authorization'] = 'Bearer ' +"eyJhbGciOiJIUzUxMiJ9.eyJhY2NvdW50X2lkIjoyNTI4MywidXNlcl9pZCI6MzE0NzEsIm9yZ19pZCI6MCwidXNlcl9rZXkiOiJmZGJmNDlmZC04MzY0LTQwNzItYjRhNy0wZTBhYThkMmNhMjYiLCJkZXZpY2VfdHlwZSI6InBjIiwidXNlcm5hbWUiOiIxNTkyNjE3MzYyNyJ9.vzt16WVd1rBjQ2jF2YlVmZxLBd3o0mhb6HT4RLhxuYTL3igUGu_FRF1VsY0JoRR-zvZK4dhpWU8hVK9We6K9dg"
    // get请求映射params参数
    if (config.method === 'get' && config.params) {
      let url = config.url + '?' + tansParams(config.params);
      url = url.slice(0, -1);
      config.params = {};
      config.url = url;
    }
    if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
      const requestObj = {
        url: config.url,
        data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
        time: new Date().getTime()
      }
      const sessionObj = cache.session.getJSON('sessionObj')
      if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
        cache.session.setJSON('sessionObj', requestObj)
      } else {
        const s_url = sessionObj.url;                  // 请求地址
        const s_data = sessionObj.data;                // 请求数据
        const s_time = sessionObj.time;                // 请求时间
        const interval = 1000;                         // 间隔时间(ms)，小于此时间视为重复提交
        // if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        //   const message = '数据正在处理，请勿重复提交';
        //   console.warn(`[${s_url}]: ` + message)
        //   return Promise.reject(new Error(message))
        // } else {
        //   cache.session.setJSON('sessionObj', requestObj)
        // }
      }
    }
    return config
  }, error => {
      console.log(error)
      Promise.reject(error)
  })

  // 响应拦截器
  service.interceptors.response.use(res => {
      // 二进制数据则直接返回
      if(isBlobResponse(res)){
        return res.data
      }

      let data
      try {
        data = decryptResponseData(res)
      } catch (error) {
        const message = error.message || '响应数据解密失败'
        Message({ message, type: 'error', duration: 5 * 1000 ,customClass:'classMessage'})
        return Promise.reject(error)
      }

      // 未设置状态码则默认成功状态
      const code = data && data.code != null ? Number(data.code) : 200;
      // 获取错误信息
      const msg = errorCode[code] || (data && data.msg) || errorCode['default']
      if (code === 401) {
        if (isSkillCenterPage()) {
          return rejectSkillCenterAuth(msg)
        }
        hintGlobal();
        return;
        if (!isRelogin.show) {
          isRelogin.show = true;
          MessageBox.confirm('登录状态已过期，您可以继续留在该页面，或者重新登录', '系统提示', { confirmButtonText: '重新登录',showCancelButton:false,showClose:false,closeOnClickModal:false,closeOnPressEscape:false, type: 'warning' }).then(() => {
            isRelogin.show = false;
            store.dispatch('LogOut').then(() => {
              location.href = '/';
            })
        }).catch(() => {
          isRelogin.show = false;
        });
      }
        return Promise.reject('无效的会话，或者会话已过期，请重新登录。')
      } else if (code === 500) {
        Message({ message: msg, type: 'error' ,customClass:'classMessage'})
        return Promise.reject(new Error(msg))
      } else if (code === 601) {
        Message({ message: msg, type: 'warning',customClass:'classMessage'})
        return Promise.reject('error')
      } else if (code !== 200) {
        Notification.error({ title: msg })
        return Promise.reject('error')
      } else {
        return data
      }
    },
    error => {
      console.log('err' + error)
      if (isSkillCenterPage() && error.response && Number(error.response.status) === 401) {
        return rejectSkillCenterAuth('登录状态已失效')
      }
      let { message } = error;
      if (message == "Network Error") {
        message = "后端接口连接异常";
      } else if (message.includes("timeout")) {
        message = "系统接口请求超时";
      } else if (message.includes("Request failed with status code")) {
        message = "系统接口" + message.substr(message.length - 3) + "异常";
      }
      Message({ message: message, type: 'error', duration: 5 * 1000 ,customClass:'classMessage'})
      return Promise.reject(error)
    }
  )

  return service;

}

export let publicRequest = createAxios ( process.env.VUE_APP_BASE_API_P );
export let request = createAxios ( process.env.VUE_APP_BASE_API );



// 通用下载方法
export function download(url, params, filename, config) {
  downloadLoadingInstance = Loading.service({ text: "正在下载数据，请稍候", spinner: "el-icon-loading", background: "rgba(0, 0, 0, 0.7)", })
  return service.post(url, params, {
    transformRequest: [(params) => { return tansParams(params) }],
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    responseType: 'blob',
    ...config
  }).then(async (data) => {
    const isLogin = await blobValidate(data);
    if (isLogin) {
      const blob = new Blob([data])
      saveAs(blob, filename)
    } else {
      const resText = await data.text();
      const rspObj = JSON.parse(resText);
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
      Message.error(errMsg);
    }
    downloadLoadingInstance.close();
  }).catch((r) => {
    console.error(r)
    Message.error('下载文件出现错误，请联系管理员！')
    downloadLoadingInstance.close();
  })
}

